import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { Button, FormActions, FormLayout, FormSection, TextArea, TextInput } from '~/components';
import { useBrandConfig } from '~/config/branding';
import { useCustomForm } from '~/hooks/useCustomForm';

/**
 * A direct port of the `required` attributes the hand-rolled `<input>` /
 * `<textarea>` carried before this form moved onto the shared controls: both
 * fields must be non-empty and the mailto is only opened once they are. Native
 * `required` rejects the empty string only, so `min(1)` matches it exactly --
 * deliberately no `.trim()`, which would rewrite the submitted subject/body.
 *
 * What changes is only where the refusal is shown: the shared controls surface
 * it as the app's own inline FieldError under the field instead of the
 * browser's grey validation bubble.
 */
const contactSchema = z.object({
  'contact-subject': z.string().min(1, 'Subject is required.'),
  'contact-message': z.string().min(1, 'Message is required.'),
});

type ContactFormValues = z.infer<typeof contactSchema>;

export const ContactUs = () => {
  const { contactEmail } = useBrandConfig();
  // Field names double as the control ids (see FormField), so they keep the
  // `contact-subject` / `contact-message` ids the raw inputs had.
  const methods = useCustomForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { 'contact-subject': '', 'contact-message': '' },
  });

  const sendEmail = (values: ContactFormValues) => {
    const mailtoHref = `mailto:${contactEmail}?subject=${encodeURIComponent(
      values['contact-subject']
    )}&body=${encodeURIComponent(values['contact-message'])}`;
    window.location.href = mailtoHref;
  };

  return (
    <div className="text-left relative isolate flex-1 overflow-hidden bg-white py-10 sm:py-32">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="text-left mb-6 sm:mb-12">
          <p className="text-base/7 font-semibold text-primary-50">Contact</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-pretty text-grey-100 sm:text-5xl">
            Contact Us
          </h1>
          <p className="mt-6 text-lg/8 text-grey-300">
            Questions, feedback, or need a hand? Send us a note and we'll get back to you. You can
            also email{' '}
            <a
              href={`mailto:${contactEmail}`}
              className="text-primary-50 hover:text-primary-t-300 underline"
            >
              {contactEmail}
            </a>{' '}
            directly.
          </p>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(sendEmail)}>
            <FormLayout>
              <FormSection componentsPerLine={1}>
                <TextInput name="contact-subject" label="Subject" required />
                <TextArea name="contact-message" label="Message" rows={6} required />
              </FormSection>
              <FormActions sticky={false}>
                <Button type="submit">Send email</Button>
              </FormActions>
            </FormLayout>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};
